import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PenLine, Check, Download, Eraser, ShieldCheck, Loader2, Package, FileSignature } from "lucide-react";
import api, { apiError, openPdf } from "../lib/api";

// Panneau de signature du contrat de remise de matériel (tablette, imprimante, etc.)
// Modelé sur SignRestoContract — signature simple électronique, valable eIDAS.
// Le contrat est généré par l'admin ; le restaurateur peut ici le lire et le signer.
export default function SignHandoverContract({ lang = "fr" }) {
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [state, setState] = useState({ available: false, signed: false, handover: null });
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get("/restaurateur/handover-contract/status");
      setState(data);
    } catch {
      setState({ available: false, signed: false, handover: null });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const getPos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: cx * (c.width / rect.width), y: cy * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true;
    const ctx = canvasRef.current.getContext("2d"); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); const p = getPos(e);
    ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#1A1A1A"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke();
    hasDrawn.current = true; };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };

  const doSign = async () => {
    if (!hasDrawn.current) { toast.error(lang === "nl" ? "Teken eerst uw handtekening" : "Dessinez d'abord votre signature"); return; }
    setSigning(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      await api.post("/restaurateur/handover-contract/sign", { signature_data_url: signature, lang });
      toast.success(lang === "nl" ? "Contract ondertekend ✅" : "Contrat signé ✅");
      await load();
      // Ne plus ouvrir automatiquement le PDF dans un nouvel onglet (perturbait la session
      // + l'imprimante Bluetooth sur tablette). Le PDF reste accessible via le bouton
      // "PDF signé" affiché juste après le passage en état signé.
    } catch (e) { toast.error(apiError(e)); }
    finally { setSigning(false); }
  };

  if (loading) return (
    <div data-testid="rt-handover-loading" className="rounded-3xl bg-white border border-[#E2DFD8] p-6 flex items-center gap-2 text-sm text-[#75807A]">
      <Loader2 className="w-4 h-4 animate-spin" /> {lang === "nl" ? "Laden..." : "Chargement..."}
    </div>
  );

  if (!state.available) return null;

  if (state.signed) {
    const signedDate = new Date(state.handover.signed_at).toLocaleString(lang === "nl" ? "nl-BE" : "fr-BE",
      { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    return (
      <section data-testid="rt-handover-signed" className="rounded-3xl bg-white border-2 border-[#16a34a]/40 p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-2xl grid place-items-center bg-[#DCFCE7]">
            <ShieldCheck className="w-6 h-6 text-[#16a34a]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">
              {lang === "nl" ? "Materiaaloverdrachtcontract ondertekend" : "Contrat de remise de matériel signé"}
            </h3>
            <p className="text-sm text-[#75807A] mt-1">
              {lang === "nl" ? "Ondertekend op" : "Signé le"} <b>{signedDate}</b>
            </p>
          </div>
        </div>
        <div className="mt-5">
          <button
            data-testid="rt-handover-download-signed"
            onClick={async () => {
              try { await openPdf("/restaurateur/handover-contract/me.pdf", "nold-go-remise-materiel-signe.pdf"); }
              catch { toast.error(lang === "nl" ? "Kan PDF niet openen" : "Impossible d'ouvrir le PDF"); }
            }}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm">
            <Download className="w-4 h-4" /> {lang === "nl" ? "Ondertekend PDF" : "PDF signé"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="rt-handover-signblock" className="rounded-3xl bg-white border-2 border-[#F26419]/40 p-6">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-11 h-11 rounded-2xl grid place-items-center bg-[#FFF3E6]">
          <FileSignature className="w-6 h-6 text-[#F26419]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-head font-black text-xl text-[#1A1A1A]">
            {lang === "nl" ? "Onderteken uw materiaaloverdrachtcontract" : "Signer mon contrat de remise de matériel"}
          </h3>
          <p className="text-sm text-[#75807A] mt-1">
            {lang === "nl"
              ? "Lees het contract en teken vervolgens met vinger of muis in het onderstaande vak."
              : "Lisez le contrat, puis signez au doigt (ou à la souris) dans le cadre ci-dessous."}
          </p>
        </div>
      </div>
      <button
        data-testid="rt-handover-preview"
        onClick={async () => {
          try { await openPdf("/restaurateur/handover-contract/preview.pdf", "contrat-remise-materiel.pdf"); }
          catch { toast.error(lang === "nl" ? "Kan PDF niet openen" : "Impossible d'ouvrir le PDF"); }
        }}
        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F26419] hover:text-[#D94D0E]">
        <Download className="w-4 h-4" /> {lang === "nl" ? "Contract lezen (PDF)" : "Lire le contrat (PDF)"}
      </button>
      <div className="mt-4">
        <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] bg-[#FDF8F0] overflow-hidden">
          <canvas ref={canvasRef} data-testid="rt-handover-canvas" width={800} height={220}
            className="w-full touch-none" style={{ height: 200, cursor: "crosshair" }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        </div>
        <div className="mt-2 flex items-center justify-between">
          <button data-testid="rt-handover-clear" onClick={clearCanvas}
            className="inline-flex items-center gap-1 text-xs text-[#75807A] hover:text-[#D32F2F]">
            <Eraser className="w-3.5 h-3.5" /> {lang === "nl" ? "Wissen" : "Effacer"}
          </button>
          <div className="text-[11px] text-[#75807A]">
            {lang === "nl" ? "Uw handtekening wordt versleuteld opgeslagen." : "Signature stockée de façon chiffrée."}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="rt-handover-submit" onClick={doSign} disabled={signing}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm disabled:opacity-60">
          {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
          {signing ? (lang === "nl" ? "Ondertekenen..." : "Signature en cours...") : (lang === "nl" ? "Ondertekenen en bevestigen" : "Signer et confirmer")}
        </button>
        <span className="inline-flex items-center gap-1 text-xs text-[#75807A]">
          <Check className="w-3.5 h-3.5 text-[#16a34a]" /> {lang === "nl" ? "eIDAS-compatibel" : "conforme eIDAS"}
        </span>
      </div>
    </section>
  );
}
